import { message } from 'antd'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'

import {
  addTempMarketplaceConnect,
  deleteMarketplaceConnect,
  editMarketplaceConnect,
  getMarketplaceConnect,
  getAllMarketplaceConnectProducts,
  getMarketplaceConnectProduct,
  getMarketplaceConnectProducts,
  getMarketplaceConnects,
  getMarketplaceConnectTokopedia,
  getMarketplaceShopeeBrandList,
  getMarketplaceShopeeCategoryAttributes,
  getMarketplaceShopeeCategories,
  getMarketplaceShopeeChannelList,
  getMarketplaceShopeeItemLimit,
  getMarketplaceShopeeSizeCharts,
  getMarketplaceShopeeSizeChartDetail,
  getMarketplaceTiktokBrandList,
  getMarketplaceTiktokCategories,
  getMarketplaceTiktokCategoryAttributes,
  getMarketplaceTiktokDeliveryOptions,
  publishPosProductToShopee,
  publishPosProductToTiktok,
  updateTiktokMarketplaceConnectProduct,
  uploadMarketplaceTiktokImage,
  marketplaceShopeeProductBoosts,
  getMarketplaceShopeeProductBoost,
  getMarketplaceShopeeBoostedProducts,
  pushMarketplaceConnectProductDetail,
  pullMarketplaceConnectProducts,
  deleteMarketplaceConnectProductListing,
  deleteMarketplaceConnectProduct,
  linkMarketplaceConnectProductPosProduct,
  generateMarketplaceConnectProductPosProduct,
  generateMarketplaceConnectProductPosProducts,
  syncMarketplaceConnect,
  syncMarketplaceConnectProducts,
  getMarketplaceChartSalesPieByMarketplace,
  getMarketplaceChartSalesBarMonthlyByMarketplace,
  getMarketplacePosProductLinkBarChart,
  updateShopeeMarketplaceConnectProduct,
  getMarketplaceConnectFailedSyncLogs,
  getAllMarketplaceConnectFailedSyncLogs,
  getMarketplaceConnectSyncHistories,
  unlistShopeeMarketplaceConnectProduct,
  listShopeeMarketplaceConnectProduct,
  activateTiktokMarketplaceConnectProduct,
  deactivateTiktokMarketplaceConnectProduct,
  getGroupedByProductMarketplaceProducts,
  updateMarketplaceConnectProductSyncStock,
  updateMarketplaceConnectProductPrice,
} from 'utils/apis'
import { financeProductMpProductsQueryKey } from 'utils/queries/product'

export const useMarketplaceConnects = ({ params, options = {} }) => {
  return useQuery(
    ['marketplaceConnects', params],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceConnects(params)
      return data
    },
    {
      onError: (error) => {
        const content = error?.data?.message || 'Failed to load data from server!'
        message.error({ content, key: content })
      },
      ...options,
    },
  )
}

export const useMarketplaceConnectTokopedia = ({ params, options }) => {
  return useQuery(
    ['marketplaceConnectTokopedia', params],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceConnectTokopedia(params)

      return data
    },
    options,
  )
}

export const useSyncMarketplaceConnect = () => {
  return useMutation(syncMarketplaceConnect)
}

export const useDeleteMarketplaceConnect = () => {
  return useMutation(deleteMarketplaceConnect)
}

export const useMarketplaceConnect = ({ id, options = {} }) => {
  return useQuery(
    ['marketplaceConnect', id],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceConnect(id)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace connection!')
      },
      ...options,
    },
  )
}

export const useAddTempMarketplaceConnect = () => {
  return useMutation(addTempMarketplaceConnect)
}

export const useEditMarketplaceConnect = () => {
  return useMutation(editMarketplaceConnect)
}

export const useMarketplaceConnectAuthUrl = (getAuthFunction) => {
  return useMutation(getAuthFunction)
}

export const useAllMarketplaceConnectProducts = ({ params, options = {} }) => {
  return useQuery(
    ['allMarketplaceConnectProducts', params],
    async () => {
      const {
        data: { data },
      } = await getAllMarketplaceConnectProducts(params)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace products!')
      },
      ...options,
    },
  )
}

export const useMarketplaceConnectProducts = ({ id, params, options = {} }) => {
  return useQuery(
    ['marketplaceConnectProducts', id, params],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceConnectProducts(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace products!')
      },
      ...options,
    },
  )
}

const invalidateMarketplaceProductLists = (queryClient) => {
  queryClient.invalidateQueries(['allMarketplaceConnectProducts'])
  queryClient.invalidateQueries(['marketplaceConnectProducts'])
}

const invalidateFinanceProductMpProducts = (queryClient, variables) => {
  const financeProductId = variables?.finance_product_id
  if (financeProductId != null && financeProductId !== '') {
    queryClient.invalidateQueries(financeProductMpProductsQueryKey(financeProductId))
  }
}

const invalidateMarketplaceConnectProductDetail = (queryClient, variables) => {
  if (variables?.connect_id != null && variables?.product_id != null) {
    queryClient.invalidateQueries([
      'marketplaceConnectProduct',
      variables.connect_id,
      variables.product_id,
    ])
  }
}

export const useSyncMarketplaceConnectProducts = () => {
  const queryClient = useQueryClient()
  return useMutation(syncMarketplaceConnectProducts, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      if (variables?.id != null) {
        queryClient.invalidateQueries(['marketplaceConnect', variables.id])
      }
    },
  })
}

export const usePushMarketplaceConnectProductDetail = () => {
  const queryClient = useQueryClient()
  return useMutation(pushMarketplaceConnectProductDetail, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const usePullMarketplaceConnectProducts = () => {
  const queryClient = useQueryClient()
  return useMutation(pullMarketplaceConnectProducts, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useDeleteMarketplaceConnectProductListing = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteMarketplaceConnectProductListing, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useDeleteMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useLinkMarketplaceConnectProductPosProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(linkMarketplaceConnectProductPosProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateMarketplaceConnectProductDetail(queryClient, variables)
      invalidateFinanceProductMpProducts(queryClient, variables)
      queryClient.invalidateQueries(['groupedByProductMarketplaceProducts'])
    },
  })
}

export const useGenerateMarketplaceConnectProductPosProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(generateMarketplaceConnectProductPosProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateMarketplaceConnectProductDetail(queryClient, variables)
      queryClient.invalidateQueries(['groupedByProductMarketplaceProducts'])
    },
  })
}

export const useGenerateMarketplaceConnectProductPosProducts = () => {
  const queryClient = useQueryClient()
  return useMutation(generateMarketplaceConnectProductPosProducts, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      queryClient.invalidateQueries(['groupedByProductMarketplaceProducts'])
      if (variables?.connect_id != null) {
        queryClient.invalidateQueries(['marketplaceConnect', variables.connect_id])
      }
    },
  })
}

export const usePublishPosProductToShopee = () => {
  const queryClient = useQueryClient()
  return useMutation(publishPosProductToShopee, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const usePatchShopeeMarketplaceConnectProductBoosts = () => {
  const queryClient = useQueryClient()
  return useMutation(marketplaceShopeeProductBoosts, {
    onSuccess: () => {
      invalidateMarketplaceProductLists(queryClient)
      queryClient.invalidateQueries(['marketplaceShopeeProductBoost'])
    },
  })
}

export const useAddShopeeProductBoost = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ connect_id, mp_product_id }) => {
      const {
        data: { data: existing },
      } = await getMarketplaceShopeeProductBoost({ connect_id })
      const items = [...(existing || [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((it, i) => ({
          mp_product_id: it.mp_product_id,
          position: i + 1,
          pinned: it.pinned ? 1 : 0,
        }))
      if (!items.some((it) => it.mp_product_id === mp_product_id)) {
        items.push({ mp_product_id, position: items.length + 1, pinned: 0 })
      }
      return marketplaceShopeeProductBoosts({ connect_id, data: { items } })
    },
    {
      onSuccess: () => {
        invalidateMarketplaceProductLists(queryClient)
        queryClient.invalidateQueries(['marketplaceShopeeProductBoost'])
      },
    },
  )
}

export const useInfiniteMarketplaceConnectProducts = ({ connectId, params = {}, options = {} }) => {
  return useInfiniteQuery(
    ['infiniteMarketplaceConnectProducts', connectId, params],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getMarketplaceConnectProducts(connectId, {
        ...params,
        page: pageParam,
        per_page: 20,
      })
      return data
    },
    {
      enabled: !!connectId,
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace products!')
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeProductBoost = ({ connectId, options = {} }) => {
  return useQuery(
    ['marketplaceShopeeProductBoost', connectId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeProductBoost({ connect_id: connectId })
      return data
    },
    {
      enabled: !!connectId,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load product boost list!')
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeBoostedProducts = ({ connectId, options = {} }) => {
  return useQuery(
    ['marketplaceShopeeBoostedProducts', connectId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeBoostedProducts({ connect_id: connectId })
      return data
    },
    {
      enabled: !!connectId,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load boosted products!')
      },
      ...options,
    },
  )
}

export const useUnlistShopeeMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(unlistShopeeMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useListShopeeMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(listShopeeMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useActivateTiktokMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(activateTiktokMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useDeactivateTiktokMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(deactivateTiktokMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useMarketplaceChartSalesBarMonthlyByMarketplace = ({ params, options = {} }) => {
  return useQuery(
    ['marketplaceChartSalesBarMonthlyByMarketplace', params],
    async () => {
      const { data } = await getMarketplaceChartSalesBarMonthlyByMarketplace(params)
      return data?.data ?? {}
    },
    {
      onError: (error) => {
        const content = error?.data?.message || 'Failed to load marketplace sales bar chart!'
        message.error({ content, key: content })
      },
      ...options,
    },
  )
}

export const useMarketplacePosProductLinkBarChart = ({ params, options = {} } = {}) => {
  return useQuery(
    ['marketplacePosProductLinkBarChart', params],
    async () => {
      const { data } = await getMarketplacePosProductLinkBarChart(params)
      return data?.data ?? {}
    },
    {
      onError: (error) => {
        const content = error?.data?.message || 'Failed to load product link bar chart!'
        message.error({ content, key: content })
      },
      ...options,
    },
  )
}

export const useMarketplaceChartSalesPieByMarketplace = ({ params, options = {} }) => {
  return useQuery(
    ['marketplaceChartSalesPieByMarketplace', params],
    async () => {
      const { data } = await getMarketplaceChartSalesPieByMarketplace(params)
      return data?.data ?? {}
    },
    {
      onError: (error) => {
        const content = error?.data?.message || 'Failed to load marketplace sales chart!'
        message.error({ content, key: content })
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeChannelList = (connectId, options = {}) => {
  return useQuery(
    ['marketplaceShopeeChannelList', connectId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeChannelList(connectId)
      return data
    },
    {
      enabled: Boolean(connectId),
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load logistics channel list!')
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeCategories = ({ lang, options = {} } = {}) => {
  return useQuery(
    ['marketplaceShopeeCategories', lang],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeCategories(lang)
      return data
    },
    {
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load Shopee categories!')
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeCategoryAttributes = ({ categoryId, lang, options = {} }) => {
  return useQuery(
    ['marketplaceShopeeCategoryAttributes', categoryId, lang],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeCategoryAttributes(categoryId, lang)
      return data
    },
    {
      enabled: !!categoryId,
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load category attributes!')
      },
      ...options,
    },
  )
}

export const useMarketplaceConnectProduct = ({ connect_id, product_id, options = {} }) => {
  return useQuery(
    ['marketplaceConnectProduct', connect_id, product_id],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceConnectProduct(connect_id, product_id)
      return data
    },
    {
      enabled: !!connect_id && !!product_id,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace product!')
      },
      ...options,
    },
  )
}

export const useUpdateShopeeMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(updateShopeeMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateMarketplaceConnectProductDetail(queryClient, variables)
    },
  })
}

const SHOPEE_SIZE_CHART_PAGE_SIZE = 20

export const useMarketplaceShopeeItemLimit = ({ connectId, categoryId, options = {} }) => {
  return useQuery(
    ['marketplaceShopeeItemLimit', connectId, categoryId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeItemLimit({
        connect_id: connectId,
        category_id: categoryId,
      })
      return data
    },
    {
      enabled: Boolean(connectId) && Boolean(categoryId),
      staleTime: 60 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load Shopee item limit!')
      },
      ...options,
    },
  )
}

export const useInfiniteMarketplaceShopeeSizeCharts = ({
  connectId,
  categoryId,
  enabled = true,
  options = {},
}) => {
  return useInfiniteQuery(
    ['marketplaceShopeeSizeCharts', connectId, categoryId],
    async ({ pageParam }) => {
      const {
        data: { data },
      } = await getMarketplaceShopeeSizeCharts({
        connect_id: connectId,
        category_id: categoryId,
        page_size: SHOPEE_SIZE_CHART_PAGE_SIZE,
        cursor: pageParam ?? undefined,
      })
      return data
    },
    {
      enabled: enabled && Boolean(connectId) && Boolean(categoryId),
      getNextPageParam: (lastPage) => lastPage?.next_cursor || undefined,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load Shopee size chart list!')
      },
      ...options,
    },
  )
}

export const useMarketplaceShopeeSizeChartDetail = ({ connectId, sizeChartId, options = {} }) => {
  return useQuery(
    ['marketplaceShopeeSizeChartDetail', connectId, sizeChartId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeSizeChartDetail({
        connect_id: connectId,
        size_chart_id: sizeChartId,
      })
      return data
    },
    {
      enabled: Boolean(connectId) && Boolean(sizeChartId),
      staleTime: 60 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load Shopee size chart detail!')
      },
      ...options,
    },
  )
}

export const useShopeeBrandList = ({ categoryId, lang, options = {} }) => {
  return useQuery(
    ['shopeeBrandList', categoryId, lang],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceShopeeBrandList({
        category_id: categoryId,
        status: 1,
        language: lang,
      })
      return data
    },
    {
      enabled: !!categoryId,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load brand list!')
      },
      ...options,
    },
  )
}

export const useInfiniteMarketplaceConnectFailedSyncLogs = ({ connectId, options = {} }) => {
  return useInfiniteQuery(
    ['marketplaceConnectFailedSyncLogs', connectId],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getMarketplaceConnectFailedSyncLogs(connectId, { page: pageParam, per_page: 20 })
      return data
    },
    {
      enabled: !!connectId,
      getNextPageParam: (lastPage) => {
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load sync logs!')
      },
      ...options,
    },
  )
}

export const useInfiniteAllMarketplaceConnectFailedSyncLogs = ({ options = {} } = {}) => {
  return useInfiniteQuery(
    ['allMarketplaceConnectFailedSyncLogs'],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getAllMarketplaceConnectFailedSyncLogs({ page: pageParam, per_page: 20 })
      return data
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load sync logs!')
      },
      ...options,
    },
  )
}

export const useGroupedByProductMarketplaceProducts = ({ params, options = {} }) => {
  return useQuery(
    ['groupedByProductMarketplaceProducts', params],
    async () => {
      const {
        data: { data },
      } = await getGroupedByProductMarketplaceProducts(params)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load marketplace products!')
      },
      ...options,
    },
  )
}

export const useUpdateMarketplaceConnectProductSyncStock = () => {
  const queryClient = useQueryClient()
  return useMutation(updateMarketplaceConnectProductSyncStock, {
    onSuccess: () => {
      queryClient.invalidateQueries(['groupedByProductMarketplaceProducts'])
    },
  })
}

export const useUpdateMarketplaceConnectProductPrice = () => {
  const queryClient = useQueryClient()
  return useMutation(updateMarketplaceConnectProductPrice, {
    onSuccess: () => {
      queryClient.invalidateQueries(['groupedByProductMarketplaceProducts'])
    },
  })
}

export const useInfiniteMarketplaceConnectSyncHistories = ({ connectId, status, options = {} }) => {
  return useInfiniteQuery(
    ['marketplaceConnectSyncHistories', connectId, status],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getMarketplaceConnectSyncHistories({
        mp_connect_id: connectId,
        status: status || undefined,
        page: pageParam,
        per_page: 15,
      })
      return data
    },
    {
      enabled: !!connectId,
      getNextPageParam: (lastPage) => {
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load sync histories!')
      },
      ...options,
    },
  )
}

const TIKTOK_BRAND_PAGE_SIZE = 20

export const useMarketplaceTiktokCategories = ({ locale, options = {} } = {}) => {
  return useQuery(
    ['marketplaceTiktokCategories', locale],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceTiktokCategories({ locale })
      return data
    },
    {
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load TikTok categories!')
      },
      ...options,
    },
  )
}

export const useMarketplaceTiktokCategoryAttributes = ({ categoryId, options = {} }) => {
  return useQuery(
    ['marketplaceTiktokCategoryAttributes', categoryId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceTiktokCategoryAttributes({ category_id: categoryId })
      return data
    },
    {
      enabled: !!categoryId,
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load TikTok category attributes!')
      },
      ...options,
    },
  )
}

export const useInfiniteTiktokBrandList = ({ categoryId, search, options = {} }) => {
  return useInfiniteQuery(
    ['tiktokBrandList', categoryId, search],
    async ({ pageParam }) => {
      const {
        data: { data },
      } = await getMarketplaceTiktokBrandList({
        category_id: categoryId,
        page_size: TIKTOK_BRAND_PAGE_SIZE,
        next_page_token: pageParam ?? undefined,
        search: search || undefined,
      })
      return data
    },
    {
      enabled: !!categoryId,
      getNextPageParam: (lastPage) => lastPage?.next_page_token || undefined,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load TikTok brand list!')
      },
      ...options,
    },
  )
}

export const useMarketplaceTiktokDeliveryOptions = ({ connectId, options = {} }) => {
  return useQuery(
    ['marketplaceTiktokDeliveryOptions', connectId],
    async () => {
      const {
        data: { data },
      } = await getMarketplaceTiktokDeliveryOptions({ connect_id: connectId })
      return data
    },
    {
      enabled: !!connectId,
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load TikTok delivery options!')
      },
      ...options,
    },
  )
}

export const useUploadMarketplaceTiktokImage = () => {
  return useMutation(uploadMarketplaceTiktokImage)
}

export const usePublishPosProductToTiktok = () => {
  const queryClient = useQueryClient()
  return useMutation(publishPosProductToTiktok, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateFinanceProductMpProducts(queryClient, variables)
    },
  })
}

export const useUpdateTiktokMarketplaceConnectProduct = () => {
  const queryClient = useQueryClient()
  return useMutation(updateTiktokMarketplaceConnectProduct, {
    onSuccess: (_data, variables) => {
      invalidateMarketplaceProductLists(queryClient)
      invalidateMarketplaceConnectProductDetail(queryClient, variables)
    },
  })
}
